export const DASHBOARD_THEMES = Object.freeze(['dark', 'light', 'system']);
export const normalizeDashboardTheme = value => DASHBOARD_THEMES.includes(value) ? value : 'light';
export const resolveDashboardTheme = (value, prefersDark = false) => value === 'system' ? (prefersDark ? 'dark' : 'light') : normalizeDashboardTheme(value);
export const dashboardThemeCacheKey = user => `assessnow-dashboard-theme:${user?.role === 'admin' ? 'admin' : `user:${user?.id}`}`;

const colors = /#[a-f\d]{3,8}\b|rgba?\(\s*[\d.]+\s*,\s*[\d.]+\s*,\s*[\d.]+(?:\s*,\s*[\d.]+)?\s*\)|\b(?:white|black)\b/gi;
function channels(value) {
  if (value === 'white') return [255, 255, 255, 1];
  if (value === 'black') return [0, 0, 0, 1];
  if (value[0] === '#') {
    let hex = value.slice(1);
    if (hex.length === 3 || hex.length === 4) hex = [...hex].map(c => c + c).join('');
    if (![6, 8].includes(hex.length)) return null;
    return [0, 2, 4].map(i => parseInt(hex.slice(i, i + 2), 16)).concat(hex.length === 8 ? parseInt(hex.slice(6), 16) / 255 : 1);
  }
  const numbers = value.match(/[\d.]+/g)?.map(Number);
  return numbers?.length >= 3 ? [...numbers.slice(0, 3), numbers[3] ?? 1] : null;
}
function token(value, role, onAction) {
  const rgba = channels(value.toLowerCase());
  if (!rgba) return value;
  const [r, g, b, alpha] = rgba;
  const max = Math.max(r, g, b), min = Math.min(r, g, b);
  const neutral = max - min < 65;
  const hue = r > g * 1.25 && r > b * 1.1 ? (g > 100 ? 'warning' : 'danger')
    : g > r * 1.15 && g > b * .8 ? 'success'
    : b > g * 1.12 ? (r > g * 1.15 ? 'purple' : 'info') : 'teal';
  const variable = name => `var(--dashboard-${name}, ${value})`;
  if (role === 'shadow') return variable('shadow');
  if (role === 'border') return variable(neutral ? 'border' : `${hue}-border`);
  if (role === 'overlay') return value;
  if (role === 'text' && onAction && min > 180) return `var(--dashboard-action-foreground, ${value})`;
  if (neutral) {
    if (role === 'background') {
      if (min > 220) return value;
      if (onAction && alpha >= .5 && max < 180) return variable('neutral-action');
      return variable(alpha < .45 || min > 40 ? 'surface-soft' : 'surface');
    }
    if (alpha < .4) return variable('grid');
    if (min < 65 && alpha >= .6) return value;
    return variable(min > 190 ? 'text' : 'muted');
  }
  if (role === 'background') {
    if (alpha < .5 || min > 150 || (!onAction && max < 130)) return variable(`${hue}-surface`);
    return variable(`${hue}-action`);
  }
  return variable(`${hue}-text`);
}

// Return CSS variables, rather than choosing a palette at render time. Existing
// inline styles and hover updates therefore change theme immediately. The
// original color is the fallback outside the educator dashboard.
export function dashboardPaint(value, role = 'text', onAction = false) {
  if (typeof value !== 'string' || value.includes('var(--dashboard-')) return value;
  if (role === 'chart') return value;
  if (role === 'scheme') return `var(--dashboard-color-scheme, ${value})`;
  if (/url\(/i.test(value)) {
    return /data:image\/svg.*%23e2e8f0/i.test(value)
      ? `var(--dashboard-select-arrow, ${value})` : value;
  }
  return value.replace(colors, color => token(color, role, onAction));
}

// Each action surface sets its own foreground. This keeps labels and SVG icons
// legible when a neutral button becomes pale, including selected-state gradients
// and styles assembled with object spreads. The dark fallback remains unchanged.
export function dashboardActionForeground(value) {
  if (typeof value !== 'string') return 'initial';
  const paint = value.match(colors)?.[0];
  const rgba = paint ? channels(paint.toLowerCase()) : null;
  if (!rgba) return 'initial';
  const [r, g, b, alpha] = rgba;
  const min = Math.min(r, g, b), max = Math.max(r, g, b);
  return max - min < 65 || alpha < .5 || min > 150
    ? 'var(--dashboard-secondary-text)' : 'initial';
}

export function dashboardCss(value) {
  return value.replace(/(background(?:-color|-image)?|color|border(?:-color)?|box-shadow|outline(?:-color)?)\s*:\s*([^;{}]+)(?=;|})/gi,
    (match, property, paint) => {
      const role = property.startsWith('background') ? 'background' : property.startsWith('border') || property.startsWith('outline') ? 'border' : property === 'box-shadow' ? 'shadow' : 'text';
      return `${property}: ${dashboardPaint(paint, role)}`;
    });
}
